import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { defineMessages, injectIntl, intlShape } from 'react-intl';
import Modal from './modal.jsx';
import Box from '../components/box/box.jsx';
import Button from '../components/button/button.jsx';
import Spinner from '../components/spinner/spinner.jsx';
import { fetchBrickLabGlobalImages } from '../reducers/bricklab-global-images';
import {
    getTeacherClasses,
    getClassBrickLabImages,
    assignBrickLabImagesToClasses,
    removeBrickLabImageFromClass,
    uploadBrickLabImages,
    initAuthFromURL
} from '../lib/seeds-api';
import { getImageDisplayUrl } from '../lib/bricklab-image-utils';
import styles from './bricklab-images-modal.css';

const messages = defineMessages({
    title: {
        defaultMessage: 'Manage Sprites',
        id: 'gui.bricklabImages.title'
    },
    manageSection: {
        defaultMessage: 'Class images',
        id: 'gui.bricklabImages.manageSection'
    },
    selectClass: {
        defaultMessage: 'Select class',
        id: 'gui.bricklabImages.selectClass'
    },
    selectCategory: {
        defaultMessage: 'Select category',
        id: 'gui.bricklabImages.selectCategory'
    },
    addImages: {
        defaultMessage: 'Add images',
        id: 'gui.bricklabImages.addImages'
    },
    remove: {
        defaultMessage: 'Remove',
        id: 'gui.bricklabImages.remove'
    },
    removing: {
        defaultMessage: 'Removing…',
        id: 'gui.bricklabImages.removing'
    },
    uploadSection: {
        defaultMessage: 'Upload new images',
        id: 'gui.bricklabImages.uploadSection'
    },
    category: {
        defaultMessage: 'Category',
        id: 'gui.bricklabImages.category'
    },
    addToClasses: {
        defaultMessage: 'Add to class(es)',
        id: 'gui.bricklabImages.addToClasses'
    },
    upload: {
        defaultMessage: 'Upload',
        id: 'gui.bricklabImages.upload'
    },
    assignTitle: {
        defaultMessage: 'Assign images to classes',
        id: 'gui.bricklabImages.assignTitle'
    },
    assign: {
        defaultMessage: 'Assign',
        id: 'gui.bricklabImages.assign'
    },
    back: {
        defaultMessage: 'Back',
        id: 'gui.bricklabImages.back'
    },
    noClassSelected: {
        defaultMessage: 'Select a class to view its images.',
        id: 'gui.bricklabImages.noClassSelected'
    },
    noImages: {
        defaultMessage: 'No images assigned yet. Use "Add images" or "Upload new images".',
        id: 'gui.bricklabImages.noImages'
    },
    loading: {
        defaultMessage: 'Loading…',
        id: 'gui.bricklabImages.loading'
    },
    chooseFiles: {
        defaultMessage: 'Choose files',
        id: 'gui.bricklabImages.chooseFiles'
    },
    chooseImages: {
        defaultMessage: 'Choose images',
        id: 'gui.bricklabImages.chooseImages'
    },
    toastImagesUploaded: {
        defaultMessage: 'Images uploaded successfully.',
        id: 'gui.bricklabImages.toastImagesUploaded'
    },
    toastImagesAssigned: {
        defaultMessage: 'Images assigned successfully.',
        id: 'gui.bricklabImages.toastImagesAssigned'
    },
    toastImageRemoved: {
        defaultMessage: 'Image removed successfully.',
        id: 'gui.bricklabImages.toastImageRemoved'
    }
});

class BrickLabImagesModal extends React.Component {
    constructor(props) {
        super(props);
        bindAll(this, [
            'handleClassChange',
            'handleAddImages',
            'handleBackFromAssign',
            'handleOpenUpload',
            'handleBackFromUpload',
            'handleAssignSubmit',
            'handleRemove',
            'handleUploadSubmit',
            'handleAssignImageToggle',
            'handleAssignClassToggle',
            'handleFileSelect',
            'handleRemoveUploadFile',
            'loadClassImages',
            'loadTeacherClasses',
            'showToast'
        ]);
        const { classId: urlClassId } = initAuthFromURL();
        const hasInitialClass = Boolean(urlClassId);
        this.state = {
            classes: [],
            classImages: [],
            selectedClassId: urlClassId || '',
            assignView: false,
            uploadView: false,
            assignSelectedImageIds: [],
            assignSelectedClassIds: [],
            uploadCategoryId: '',
            uploadClassIds: [],
            uploadFiles: [],
            uploadPreviewUrls: [],
            loadingClasses: true,
            loadingClassImages: hasInitialClass,
            loadingAssign: false,
            loadingUpload: false,
            removingImageIds: [],
            toastMessage: null,
            error: null
        };
        this.toastTimeout = null;
    }

    componentDidMount() {
        this.loadTeacherClasses();
        // Refetch when Manage Sprites opens (modal remounts). Keeps category dropdown in sync with admin changes.
        if (!this.props.bricklabGlobalImages.loading) {
            this.props.dispatch(fetchBrickLabGlobalImages(true));
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.selectedClassId && this.state.selectedClassId !== prevState.selectedClassId) {
            this.loadClassImages(this.state.selectedClassId);
        }
    }

    componentWillUnmount() {
        if (this.toastTimeout) clearTimeout(this.toastTimeout);
    }

    showToast(message) {
        if (this.toastTimeout) clearTimeout(this.toastTimeout);
        this.setState({ toastMessage: message });
        this.toastTimeout = setTimeout(() => {
            this.toastTimeout = null;
            this.setState({ toastMessage: null });
        }, 3500);
    }

    loadTeacherClasses() {
        this.setState({ loadingClasses: true, error: null });
        getTeacherClasses()
            .then(data => {
                const classes = data.classes || [];
                let selectedClassId = this.state.selectedClassId;
                if (!selectedClassId && classes.length > 0) {
                    selectedClassId = classes[0].id;
                }
                this.setState({
                    classes,
                    selectedClassId,
                    loadingClasses: false
                }, () => {
                    if (selectedClassId) this.loadClassImages(selectedClassId);
                });
            })
            .catch(err => {
                this.setState({
                    loadingClasses: false,
                    error: err.message
                });
            });
    }

    loadClassImages(classId) {
        if (!classId) return;
        this.setState({ loadingClassImages: true });
        getClassBrickLabImages(classId)
            .then(data => {
                this.setState({
                    classImages: data.images || [],
                    loadingClassImages: false
                });
            })
            .catch(err => {
                this.setState({
                    classImages: [],
                    loadingClassImages: false,
                    error: err.message
                });
            });
    }

    handleClassChange(e) {
        this.setState({ selectedClassId: e.target.value || '' });
    }

    handleAddImages() {
        this.setState({
            assignView: true,
            assignSelectedImageIds: [],
            assignSelectedClassIds: this.state.selectedClassId ? [this.state.selectedClassId] : [],
            error: null
        });
    }

    handleBackFromAssign() {
        this.setState({ assignView: false });
    }

    handleAssignImageToggle(imageId) {
        this.setState(prev => ({
            assignSelectedImageIds: prev.assignSelectedImageIds.includes(imageId)
                ? prev.assignSelectedImageIds.filter(id => id !== imageId)
                : [...prev.assignSelectedImageIds, imageId]
        }));
    }

    handleAssignClassToggle(classId) {
        this.setState(prev => ({
            assignSelectedClassIds: prev.assignSelectedClassIds.includes(classId)
                ? prev.assignSelectedClassIds.filter(id => id !== classId)
                : [...prev.assignSelectedClassIds, classId]
        }));
    }

    handleAssignSubmit() {
        const { assignSelectedImageIds, assignSelectedClassIds } = this.state;
        if (assignSelectedImageIds.length === 0 || assignSelectedClassIds.length === 0) return;
        this.setState({ loadingAssign: true, error: null });
        assignBrickLabImagesToClasses(assignSelectedClassIds, assignSelectedImageIds)
            .then(() => {
                this.setState({ loadingAssign: false, assignView: false });
                this.showToast(this.props.intl.formatMessage(messages.toastImagesAssigned));
                if (this.state.selectedClassId) {
                    this.loadClassImages(this.state.selectedClassId);
                }
            })
            .catch(err => {
                this.setState({ loadingAssign: false, error: err.message });
            });
    }

    handleRemove(imageId) {
        const { selectedClassId } = this.state;
        if (!selectedClassId) return;
        this.setState(prev => ({
            removingImageIds: [...prev.removingImageIds, imageId],
            error: null
        }));
        removeBrickLabImageFromClass(selectedClassId, imageId)
            .then(() => {
                this.setState(prev => ({
                    classImages: prev.classImages.filter(img => img.id !== imageId),
                    removingImageIds: prev.removingImageIds.filter(id => id !== imageId)
                }));
                this.showToast(this.props.intl.formatMessage(messages.toastImageRemoved));
            })
            .catch(err => {
                this.setState(prev => ({
                    error: err.message,
                    removingImageIds: prev.removingImageIds.filter(id => id !== imageId)
                }));
            });
    }

    handleOpenUpload() {
        this.setState({
            uploadView: true,
            uploadClassIds: [],
            error: null
        });
    }

    handleBackFromUpload() {
        (this.state.uploadPreviewUrls || []).forEach(url => {
            if (url) try { URL.revokeObjectURL(url); } catch (_) { }
        });
        this.setState({
            uploadView: false,
            uploadFiles: [],
            uploadPreviewUrls: []
        });
    }

    handleFileSelect(e) {
        const files = Array.from(e.target.files || []);
        (this.state.uploadPreviewUrls || []).forEach(url => {
            if (url) try { URL.revokeObjectURL(url); } catch (_) { }
        });
        const urls = files.map(f => {
            try { return URL.createObjectURL(f); } catch (_) { return null; }
        });
        this.setState({ uploadFiles: files, uploadPreviewUrls: urls });
        e.target.value = '';
    }

    handleRemoveUploadFile(index) {
        const urls = [...(this.state.uploadPreviewUrls || [])];
        const url = urls[index];
        if (url) try { URL.revokeObjectURL(url); } catch (_) { }
        urls.splice(index, 1);
        const files = this.state.uploadFiles.filter((_, i) => i !== index);
        this.setState({ uploadFiles: files, uploadPreviewUrls: urls });
    }

    handleUploadSubmit() {
        const { uploadCategoryId, uploadClassIds, uploadFiles } = this.state;
        if (!uploadCategoryId || !uploadFiles.length) return;
        const classIdsToAssign = uploadClassIds || [];
        const files = uploadFiles.map(f => ({ file: f, name: f.name }));
        this.setState({ loadingUpload: true, error: null });
        uploadBrickLabImages(uploadCategoryId, files)
            .then(data => {
                const newImageIds = (data.images || []).map(img => img.id);
                if (newImageIds.length > 0 && classIdsToAssign.length > 0) {
                    return assignBrickLabImagesToClasses(classIdsToAssign, newImageIds)
                        .then(() => {
                            (this.state.uploadPreviewUrls || []).forEach(url => {
                                if (url) try { URL.revokeObjectURL(url); } catch (_) { }
                            });
                            this.setState({
                                loadingUpload: false,
                                uploadView: false,
                                uploadFiles: [],
                                uploadPreviewUrls: [],
                                uploadClassIds: []
                            });
                            this.showToast(this.props.intl.formatMessage(messages.toastImagesUploaded));
                            if (this.state.selectedClassId && classIdsToAssign.includes(this.state.selectedClassId)) {
                                this.loadClassImages(this.state.selectedClassId);
                            }
                            this.props.dispatch(fetchBrickLabGlobalImages(true));
                        });
                }
                (this.state.uploadPreviewUrls || []).forEach(url => {
                    if (url) try { URL.revokeObjectURL(url); } catch (_) { }
                });
                this.setState({
                    loadingUpload: false,
                    uploadView: false,
                    uploadFiles: [],
                    uploadPreviewUrls: [],
                    uploadClassIds: []
                });
                this.showToast(this.props.intl.formatMessage(messages.toastImagesUploaded));
                this.props.dispatch(fetchBrickLabGlobalImages(true));
            })
            .catch(err => {
                this.setState({ loadingUpload: false, error: err.message });
            });
    }

    renderAssignView() {
        const { intl } = this.props;
        const { assignSelectedImageIds, assignSelectedClassIds, classes, loadingAssign, error } = this.state;
        const fullState = { scratchGui: { bricklabGlobalImages: this.props.bricklabGlobalImages } };
        const categories = (this.props.bricklabGlobalImages && this.props.bricklabGlobalImages.categories) || [];

        return (
            <div className={styles.assignView}>
                {error && <div className={styles.error}>{error}</div>}
                <div className={styles.assignPanels}>
                    <div className={styles.assignPanel}>
                        <div className={styles.assignPanelHeader}>
                            {intl.formatMessage(messages.assignTitle)}
                        </div>
                        <div className={styles.assignPanelBody}>
                            <div className={styles.assignImageGrid}>
                                {categories.map(cat => (cat.images || []).map(img => {
                                    const url = getImageDisplayUrl(img, fullState);
                                    const selected = assignSelectedImageIds.includes(img.id);
                                    return (
                                        <label
                                            key={img.id}
                                            className={`${styles.assignImageCard} ${selected ? styles.selected : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => this.handleAssignImageToggle(img.id)}
                                                className={styles.assignImageCheckbox}
                                            />
                                            <div className={styles.assignImageCardWrap}>
                                                {url && <img src={url} alt={img.name} className={styles.assignImageThumb} />}
                                                <span className={styles.assignImageName}>{img.name}</span>
                                            </div>
                                        </label>
                                    );
                                }))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.assignPanel}>
                        <div className={styles.assignPanelHeader}>
                            {intl.formatMessage(messages.selectClass)}
                        </div>
                        <div className={styles.assignPanelBody}>
                            <div className={styles.assignClassList}>
                                {classes.map(c => {
                                    const selected = assignSelectedClassIds.includes(c.id);
                                    const label = c.name || `${c.subject || ''} ${c.grade || ''} ${c.section || ''}`.trim() || c.id;
                                    return (
                                        <label
                                            key={c.id}
                                            className={`${styles.assignClassItem} ${selected ? styles.selected : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => this.handleAssignClassToggle(c.id)}
                                            />
                                            <span className={styles.assignClassLabel}>{label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.assignFooter}>
                    <button
                        type="button"
                        className={styles.assignButtonPrimary}
                        disabled={assignSelectedImageIds.length === 0 || assignSelectedClassIds.length === 0 || loadingAssign}
                        onClick={this.handleAssignSubmit}
                    >
                        {loadingAssign ? intl.formatMessage(messages.loading) : intl.formatMessage(messages.assign)}
                    </button>
                </div>
            </div>
        );
    }

    renderUploadView() {
        const { intl } = this.props;
        const {
            uploadCategoryId,
            uploadClassIds,
            uploadFiles,
            uploadPreviewUrls,
            uploadClassIds: stateUploadClassIds,
            loadingUpload,
            error,
            classes
        } = this.state;
        const categories = (this.props.bricklabGlobalImages && this.props.bricklabGlobalImages.categories) || [];
        const currentClassIds = stateUploadClassIds || [];

        return (
            <div className={styles.assignView}>
                {error && <div className={styles.error}>{error}</div>}
                <div className={styles.assignPanels}>
                    <div className={styles.assignPanel}>
                        <div className={styles.assignPanelHeader}>
                            {intl.formatMessage(messages.uploadSection)}
                        </div>
                        <div className={styles.assignPanelBody}>
                            <div className={styles.uploadHeaderRow}>
                                <label className={styles.uploadLabel}>
                                    <span className={styles.uploadLabelText}>{intl.formatMessage(messages.category)}</span>
                                    <select
                                        className={styles.select}
                                        value={uploadCategoryId}
                                        onChange={e => this.setState({ uploadCategoryId: e.target.value })}
                                    >
                                        <option value="">{intl.formatMessage(messages.selectCategory)}</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <div className={styles.fileInputWrap}>
                                    <span className={styles.fileInputButton}>
                                        {uploadFiles.length > 0
                                            ? `${uploadFiles.length} file(s) chosen`
                                            : intl.formatMessage(messages.chooseImages)}
                                    </span>
                                    <input
                                        type="file"
                                        multiple
                                        accept=".svg,.png,.bmp,.jpg,.jpeg,.gif"
                                        onChange={this.handleFileSelect}
                                    />
                                </div>
                            </div>
                            {uploadFiles.length > 0 && (
                                <div className={styles.uploadPreviewSection}>
                                    <div className={styles.uploadPreviewGrid}>
                                        {uploadFiles.map((file, i) => (
                                            <div key={`${file.name}-${i}`} className={styles.uploadPreviewCard}>
                                                <button
                                                    type="button"
                                                    className={styles.uploadPreviewRemove}
                                                    onClick={e => { e.preventDefault(); this.handleRemoveUploadFile(i); }}
                                                    aria-label={intl.formatMessage(messages.remove)}
                                                >
                                                    ×
                                                </button>
                                                {uploadPreviewUrls[i] ? (
                                                    <img src={uploadPreviewUrls[i]} alt={file.name} className={styles.uploadPreviewThumb} />
                                                ) : (
                                                    <div className={styles.uploadPreviewPlaceholder} />
                                                )}
                                                <span className={styles.assignImageName}>{file.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.assignPanel}>
                        <div className={styles.assignPanelHeader}>
                            {intl.formatMessage(messages.addToClasses)}
                        </div>
                        <div className={styles.assignPanelBody}>
                            <div className={styles.assignClassList}>
                                {classes.map(c => {
                                    const selected = currentClassIds.includes(c.id);
                                    const label = c.name || `${c.subject || ''} ${c.grade || ''} ${c.section || ''}`.trim() || c.id;
                                    return (
                                        <label
                                            key={c.id}
                                            className={`${styles.assignClassItem} ${selected ? styles.selected : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => {
                                                    const base = stateUploadClassIds || [];
                                                    this.setState({
                                                        uploadClassIds: selected
                                                            ? base.filter(id => id !== c.id)
                                                            : [...base, c.id]
                                                    });
                                                }}
                                            />
                                            <span className={styles.assignClassLabel}>{label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.assignFooter}>
                    <button
                        type="button"
                        className={styles.assignButtonPrimary}
                        disabled={!uploadCategoryId || uploadFiles.length === 0 || loadingUpload}
                        onClick={this.handleUploadSubmit}
                    >
                        {loadingUpload ? intl.formatMessage(messages.loading) : intl.formatMessage(messages.upload)}
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { intl, onRequestClose } = this.props;
        const {
            assignView,
            uploadView,
            classes,
            classImages,
            selectedClassId,
            loadingClasses,
            loadingClassImages,
            uploadCategoryId,
            uploadClassIds,
            uploadFiles,
            loadingUpload,
            removingImageIds,
            toastMessage,
            error
        } = this.state;

        const fullState = { scratchGui: { bricklabGlobalImages: this.props.bricklabGlobalImages } };
        const categories = (this.props.bricklabGlobalImages && this.props.bricklabGlobalImages.categories) || [];
        const { classId: urlClassId } = initAuthFromURL();
        const defaultUploadClassIds = urlClassId ? [urlClassId] : (selectedClassId ? [selectedClassId] : []);

        const handleModalBack = uploadView
            ? this.handleBackFromUpload
            : assignView
                ? this.handleBackFromAssign
                : onRequestClose;

        if (uploadView) {
            return (
                <Modal
                    fullScreen
                    contentLabel={intl.formatMessage(messages.title)}
                    id="bricklabImages"
                    onRequestClose={handleModalBack}
                >
                    <div className={styles.body}>
                        {toastMessage && (
                            <div className={styles.toast} role="status">
                                {toastMessage}
                            </div>
                        )}
                        {this.renderUploadView()}
                    </div>
                </Modal>
            );
        }

        if (assignView) {
            return (
                <Modal
                    fullScreen
                    contentLabel={intl.formatMessage(messages.title)}
                    id="bricklabImages"
                    onRequestClose={handleModalBack}
                >
                    <div className={styles.body}>
                        {toastMessage && (
                            <div className={styles.toast} role="status">
                                {toastMessage}
                            </div>
                        )}
                        {this.renderAssignView()}
                    </div>
                </Modal>
            );
        }

        return (
            <Modal
                fullScreen
                contentLabel={intl.formatMessage(messages.title)}
                id="bricklabImages"
                onRequestClose={onRequestClose}
            >
                <div className={styles.body}>
                    {toastMessage && (
                        <div className={styles.toast} role="status">
                            {toastMessage}
                        </div>
                    )}
                    {error && <div className={styles.error}>{error}</div>}

                    <section className={styles.section}>
                        <h3 className={styles.sectionTitle}>{intl.formatMessage(messages.manageSection)}</h3>
                        <div className={styles.row}>
                            <label className={styles.label}>
                                {!loadingClasses && intl.formatMessage(messages.selectClass)}
                                {!loadingClasses && (
                                    <select
                                        className={styles.select}
                                        value={selectedClassId}
                                        onChange={this.handleClassChange}
                                    >
                                        <option value="">{intl.formatMessage(messages.selectClass)}</option>
                                        {classes.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name || `${c.subject || ''} ${c.grade || ''} ${c.section || ''}`.trim() || c.id}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </label>
                            <div className={styles.mainActionButtons}>
                                <Button onClick={this.handleAddImages} className={styles.addImagesButton}>{intl.formatMessage(messages.addImages)}</Button>
                                <Button onClick={this.handleOpenUpload} className={styles.addImagesButton}>{intl.formatMessage(messages.uploadSection)}</Button>
                            </div>
                        </div>
                        {!loadingClasses && !selectedClassId && (
                            <p className={styles.hint}>{intl.formatMessage(messages.noClassSelected)}</p>
                        )}
                        {(loadingClasses || selectedClassId) && (
                            <div className={styles.tableWrap}>
                                {(loadingClasses || loadingClassImages) ? (
                                    <div className={styles.skeletonWrapper}>
                                        <div className={styles.skeletonTable}>
                                            {[1, 2, 3, 4, 5].map(i => (
                                                <div key={i} className={styles.skeletonRow}>
                                                    <div className={styles.skeletonThumb} />
                                                    <div className={styles.skeletonLine} />
                                                    <div className={styles.skeletonLineShort} />
                                                    <div className={styles.skeletonLineShort} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : classImages.length === 0 ? (
                                    <p className={styles.noImagesHint}>{intl.formatMessage(messages.noImages)}</p>
                                ) : (
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Name</th>
                                                <th>Category</th>
                                                <th>Assigned</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {classImages.map(img => {
                                                const isRemoving = removingImageIds.includes(img.id);
                                                return (
                                                    <tr key={img.id}>
                                                        <td>
                                                            {img.imageUrl && (
                                                                <img src={img.imageUrl} alt={img.name} className={styles.thumb} />
                                                            )}
                                                        </td>
                                                        <td>{img.name}</td>
                                                        <td>{img.categoryName || '—'}</td>
                                                        <td>{img.assignedAt ? new Date(img.assignedAt).toLocaleDateString() : '—'}</td>
                                                        <td>
                                                            <span className={styles.removeButtonWrap}>
                                                                {isRemoving && <Spinner small level="primary" className={styles.removeSpinner} />}
                                                                <Button
                                                                    disabled={isRemoving}
                                                                    onClick={() => this.handleRemove(img.id)}
                                                                >
                                                                    {isRemoving ? intl.formatMessage(messages.removing) : intl.formatMessage(messages.remove)}
                                                                </Button>
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}
                    </section>
                </div>
            </Modal>
        );
    }
}

BrickLabImagesModal.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    bricklabGlobalImages: PropTypes.shape({
        categories: PropTypes.array,
        loading: PropTypes.bool,
        imageUrlMap: PropTypes.object
    })
};

const mapStateToProps = state => ({
    bricklabGlobalImages: state.scratchGui.bricklabGlobalImages
});

export default injectIntl(connect(mapStateToProps)(BrickLabImagesModal));
